import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminQuestions = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState({
    category: "antiscalants",
    question_number: "",
    question_text: "",
    criteria_key: "",
    options: ""
  });

  useEffect(() => {
    const auth = localStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return;
    }
    fetchQuestions();
  }, []);

  const getAuthHeader = () => {
    const auth = localStorage.getItem("adminAuth");
    return auth ? { Authorization: `Basic ${auth}` } : {};
  };

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/questions`);
      setQuestions(response.data);
    } catch (error) {
      console.error("Error fetching questions:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      category: "antiscalants",
      question_number: "",
      question_text: "",
      criteria_key: "",
      options: ""
    });
    setEditingQuestion(null);
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setFormData({
      category: question.category,
      question_number: question.question_number.toString(),
      question_text: question.question_text,
      criteria_key: question.criteria_key,
      options: JSON.stringify(question.options, null, 2)
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        category: formData.category,
        question_number: parseInt(formData.question_number),
        question_text: formData.question_text,
        criteria_key: formData.criteria_key,
        options: JSON.parse(formData.options)
      };

      if (editingQuestion) {
        await axios.put(
          `${API}/admin/questions/${editingQuestion.id}`,
          payload,
          { headers: getAuthHeader() }
        );
        toast.success("Question updated successfully");
      } else {
        await axios.post(
          `${API}/admin/questions`,
          payload,
          { headers: getAuthHeader() }
        );
        toast.success("Question created successfully");
      }

      setDialogOpen(false);
      resetForm();
      fetchQuestions();
    } catch (error) {
      console.error("Error saving question:", error);
      toast.error("Failed to save question. Check JSON format.");
    }
  };

  const handleDelete = async (questionId) => {
    if (!window.confirm("Are you sure you want to delete this question?")) return;

    try {
      await axios.delete(`${API}/admin/questions/${questionId}`, {
        headers: getAuthHeader()
      });
      toast.success("Question deleted");
      fetchQuestions();
    } catch (error) {
      console.error("Error deleting question:", error);
      toast.error("Failed to delete question");
    }
  };

  const groupedQuestions = questions.reduce((acc, q) => {
    if (!acc[q.category]) acc[q.category] = [];
    acc[q.category].push(q);
    return acc;
  }, {});

  return (
    <AdminLayout>
      <div data-testid="admin-questions-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Questions</h1>
            <p className="text-slate-600">Manage questionnaire items</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="bg-[#00BCD4] hover:bg-[#00acc1]"
                data-testid="add-question-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Question
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingQuestion ? "Edit Question" : "Add New Question"}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <select
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full border border-slate-300 rounded px-3 py-2"
                    >
                      <option value="antiscalants">Antiscalants</option>
                      <option value="antifoulants">Antifoulants</option>
                      <option value="cleaners">Cleaners</option>
                      <option value="preservatives">Preservatives</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="question_number">Question Number</Label>
                    <Input
                      id="question_number"
                      type="number"
                      value={formData.question_number}
                      onChange={(e) => setFormData({...formData, question_number: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="question_text">Question Text</Label>
                  <Input
                    id="question_text"
                    value={formData.question_text}
                    onChange={(e) => setFormData({...formData, question_text: e.target.value})}
                    placeholder="What is your primary water source?"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="criteria_key">Criteria Key</Label>
                  <Input
                    id="criteria_key"
                    value={formData.criteria_key}
                    onChange={(e) => setFormData({...formData, criteria_key: e.target.value})}
                    placeholder="water_source"
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Used to match answers with product matching criteria
                  </p>
                </div>

                <div>
                  <Label htmlFor="options">Options (JSON array)</Label>
                  <Textarea
                    id="options"
                    value={formData.options}
                    onChange={(e) => setFormData({...formData, options: e.target.value})}
                    rows={10}
                    className="font-mono text-sm"
                    placeholder={`[
  {"id": "opt1", "text": "Municipal Water", "value": "municipal", "icon": "Building2"},
  {"id": "opt2", "text": "Well Water", "value": "well", "icon": "Waves"}
]`}
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Available icons: Building2, Waves, CloudRain, Ship, Droplets, TrendingDown, Minus, TrendingUp, AlertTriangle, Layers, Square, Filter, HelpCircle
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#00BCD4] hover:bg-[#00acc1]">
                    {editingQuestion ? "Update" : "Create"} Question
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Questions List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <p className="text-slate-600 mb-4">No questions found. Click "Add Question" or seed the database.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedQuestions).map(([category, categoryQuestions]) => (
              <div key={category} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-800 capitalize">{category}</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {categoryQuestions
                    .sort((a, b) => a.question_number - b.question_number)
                    .map((question) => (
                      <div 
                        key={question.id} 
                        className="p-4 flex items-center justify-between hover:bg-slate-50"
                        data-testid={`question-row-${question.id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="w-8 h-8 rounded-full bg-[#00BCD4] text-white flex items-center justify-center text-sm font-bold">
                              {question.question_number}
                            </span>
                            <span className="font-medium text-slate-800">
                              {question.question_text}
                            </span>
                          </div>
                          <div className="ml-11 flex items-center gap-4 text-sm text-slate-500">
                            <span>Key: <code className="bg-slate-100 px-1 rounded">{question.criteria_key}</code></span>
                            <span>{question.options?.length || 0} options</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(question)}
                            data-testid={`edit-question-${question.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(question.id)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-question-${question.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminQuestions;
